import {
  hideButtonLoader,
  hideElement,
  runSafe,
  showButtonLoader,
  showElement,
  toggleElementVisibility,
  whenReady
} from "../utils";
import "regenerator-runtime/runtime.js";
import { SignInNextStepResponse, SignInResponse, SignInStatus, SignInStep, SignInStrategy } from "../api/user/types";
import { SignIn } from "../api/user/SignIn";
import { clearFormInputsErrors, hideFormError, showFormError, updateFormInputsErrors } from "../utils/_form-utils";
import { CooldownButton } from "../components/CooldownButton";
import { HTMLElementWithOTC } from "../components/OneTimeCode";
import { getCaptchaToken } from "../google/recaptcha";
import { sanitizeInlineTextHtml } from "../utils/_sanitize-html";

type Data = Record<string, string>;

type UserNavigator = {
  navigator: string,
};

type UserNavigatorSources = {
  document: Document;
  history: History;
  navigator: Navigator;
  screen: Screen;
};

type UserNavigatorFields = {
  [K in keyof UserNavigatorSources]: (keyof UserNavigatorSources[K])[];
};

whenReady(() => {
  runSafe(async () => {
    initCloseButton();

    const form = getAuthForm();
    const resendCodeButton = getResendCodeButton(form);

    form.addEventListener("submit", async (e: SubmitEvent): Promise<void> => {
      e.preventDefault();

      addLoaderToSubmitButton(form);

      try {
        switch (currentStep) {
          case SignInStep.EMAIL_OR_PHONE_ENTERING:
            await confirmEmailOrPhoneEntering();
            break;
          case SignInStep.CAPTCHA:
            await confirmCaptcha();
            break;
          case SignInStep.EMAIL_OTP:
          case SignInStep.PHONE_OTP:
            await confirmOtp(currentStep)
            break;
        }

        hideFormError(form);
      } catch (e) {
        showFormError(form);
        console.error("Form submission failed at step:", currentStep, e);
      } finally {
        removeLoaderFromSubmitButton(form);
      }
    });

    form.addEventListener("OTCSubmit", async (): Promise<void> => {
      try {
        await confirmOtp(currentStep);

        hideFormError(form);
      } catch (error) {
        showFormError(form);
        console.error("OTP confirmation failed at step:", currentStep, error);

        const otcInstance = getOneTimeCode(form).__otcInstance;
        otcInstance.reset();
      }
    });

    resendCodeButton.addEventListener("click", async (e: PointerEvent): Promise<void> => {
      if (!(e.target instanceof HTMLButtonElement)) {
        return;
      }

      showButtonLoader(e.target);

      try {
        await resendOtp();

        hideFormError(form);
      } catch (e) {
        showFormError(form);
        console.error("Resending OTP failed at step:", currentStep, e);
      } finally {
        hideButtonLoader(e.target);
      }
    });

    // works together with CSS rules (:has + pointer-events: none)
    getYandexButtonContainer()?.addEventListener("click", () => {
      const privacyConsentCheckbox = getPrivacyConsentCheckbox(form);

      if (!privacyConsentCheckbox.checked) {
        privacyConsentCheckbox.reportValidity();
      }
    })

    const defaultAuthStrategy: SignInStrategy = SignInStrategy.OTP;

    const setAuthStateToDefault = (): void => {
      updateAuthStrategy(defaultAuthStrategy);
      hideFormError(form);
      showStep(SignInStep.EMAIL_OR_PHONE_ENTERING);
    };

    getOtherButton(form)?.addEventListener("click", setAuthStateToDefault);
    getBackButton(form)?.addEventListener("click", setAuthStateToDefault);

    const prepareSignUpData = createSignUpDataPreparer();

    let currentStep: SignInStep = SignInStep.EMAIL_OR_PHONE_ENTERING;
    let authStrategy: SignInStrategy = defaultAuthStrategy;

    const initYandexAuth = () => {
      initYandexAuthSDK(prepareSignUpData())
        .then((response) => {
          if (!response) {
            return;
          }

          updateAuthStrategy(SignInStrategy.YANDEX);
          switch (response.status) {
            case SignInStatus.SUCCESS:
              window.location.reload();
              break;
            case SignInStatus.NEXT_STEP:
              showStep(response.step, response);
              break;
            case SignInStatus.FAILED:
              updateFormInputsErrors(form, response.errors);
              break;
            default:
              throw new Error("Unexpected response status");
          }
        })
        .catch((error) => {
          console.error("Init yandex auth error:", error);
        });
    }
    initYandexAuth();

    const confirmEmailOrPhoneEntering = async (): Promise<void> => {
      // It is necessary because of yandex reinit could change strategy
      updateAuthStrategy(defaultAuthStrategy);

      const captchaToken = await getCaptchaToken("sign_in");
      const response = await SignIn.signIn(authStrategy, SignInStep.EMAIL_OR_PHONE_ENTERING, prepareSignUpData(form), captchaToken);

      switch (response.status) {
        case SignInStatus.FAILED:
          updateFormInputsErrors(form, response.errors)
          break;
        case SignInStatus.NEXT_STEP:
          showStep(response.step, response);
          break;
      }
    }

    const confirmCaptcha = async (): Promise<void> => {
      const response = await SignIn.signIn(authStrategy, SignInStep.CAPTCHA, prepareSignUpData(form));

      switch (response.status) {
        case SignInStatus.FAILED:
          updateFormInputsErrors(form, response.errors)
          break;
        case SignInStatus.NEXT_STEP:
          showStep(response.step, response);
          reloadCaptcha(getCaptcha(form));
          break;
      }
    }

    const confirmOtp = async (step: SignInStep): Promise<void> => {
      const response = await SignIn.signIn(authStrategy, step, prepareSignUpData(form));

      const otcInstance = getOneTimeCode(form).__otcInstance;

      switch (response.status) {
        case SignInStatus.SUCCESS:
          showSuccess(form);
          window.location.reload();
          break;
        case SignInStatus.NEXT_STEP:
          otcInstance.showSuccess();
          showStep(response.step, response);
          break;
        case SignInStatus.FAILED:
          otcInstance.showError(response.errors.code);
          break;
      }
    };

    const resendOtp = async (): Promise<void> => {
      const response = await SignIn.signIn(authStrategy, getPreviousStep(currentStep, authStrategy), prepareSignUpData(form));

      switch (response.status) {
        case SignInStatus.NEXT_STEP:
          if (response.step === SignInStep.CAPTCHA) {
            showStep(response.step, response);
          }
          const cooldownButtonInstance = CooldownButton.getInstance(resendCodeButton);
          cooldownButtonInstance?.restart(response.otp.cooldownSeconds);
          break;
        case SignInStatus.FAILED:
          updateFormInputsErrors(form, response.errors);
          break;
      }
    };

    const showStep = (stepToShow: SignInStep, response: SignInNextStepResponse | null = null): void => {
      clearFormInputsErrors(form);

      const step = response?.step ?? stepToShow;
      updateCurrentStep(step);

      switch (currentStep) {
        case SignInStep.EMAIL_OR_PHONE_ENTERING:
          showEmailOrPhoneEnteringStep(form);
          reinitYandexAuth();
          break;
        case SignInStep.EMAIL_OTP:
        case SignInStep.EMAIL_OTP_FOR_LOGIN_BY_PHONE:
        case SignInStep.PHONE_OTP:
        case SignInStep.PHONE_OTP_FOR_LOGIN_BY_EMAIL:
          if (response === null) {
            throw new Error("Next step response is required for this step");
          }

          showOtpStep(form, response);
          break;
        case SignInStep.PHONE_OTP_UNAVAILABLE:
          showPhoneOtpUnavailableStep(form);
          break;
        case SignInStep.CAPTCHA:
          showCaptchaStep(form);
          break;
      }
    };

    const updateCurrentStep = (step: SignInStep): void => {
      currentStep = step;
    };
    const updateAuthStrategy = (strategy: SignInStrategy): void => {
      authStrategy = strategy;
    };

    const reinitYandexAuth = () => {
      if (!isYandexAuthInit()) {
        initYandexAuth();
        observeYandexAuthButtonRemoving();
      }
    }

    let observer: MutationObserver | null = null;

    const observeYandexAuthButtonRemoving = () => {
      observer?.disconnect();

      const yandexAuthButton = getYandexAuthButton();

      if (!(yandexAuthButton instanceof HTMLElement)) {
        return;
      }

      const callback: MutationCallback = (mutationList, observer) => {
        for (const mutation of mutationList) {
          if (mutation.type === "childList") {
            const isIframeRemoved = Array.from(mutation.removedNodes)
              .some(node => node instanceof HTMLElement && node.tagName.toLowerCase() === 'iframe');

            if (isIframeRemoved) {
              initYandexAuth();
              observer.disconnect();
            }
          }
        }
      };

      observer = new MutationObserver(callback);
      observer.observe(yandexAuthButton, { childList: true, subtree: true });
    }
  });
});

function getYandexAuthButton(): HTMLElement | null {
  return document.getElementById("ya-auth-button");
}

function isYandexAuthInit() {
  const button = getYandexAuthButton();
  return button !== null && button.querySelector("iframe") !== null;
}

function reloadCaptcha(captcha: HTMLElement): void {
  captcha.querySelector<HTMLImageElement>('#captcha-image')?.click();
}

function createSignUpDataPreparer(): (form?: HTMLFormElement) => FormData {
  const initialUserSignUpData = getUserData();

  return (form?: HTMLFormElement) => {
    const formData = new FormData(form);

    for (const [key, value] of Object.entries(initialUserSignUpData)) {
      formData.set(key, value.toString());
    }

    return formData as FormData;
  }
}

function getPreviousStep(step: SignInStep, strategy: SignInStrategy): SignInStep {
  switch (step) {
    case SignInStep.EMAIL_OTP_FOR_LOGIN_BY_PHONE:
      return SignInStep.PHONE_OTP;
    case SignInStep.PHONE_OTP_FOR_LOGIN_BY_EMAIL:
      return SignInStep.EMAIL_OTP;
    case SignInStep.EMAIL_OTP:
    case SignInStep.PHONE_OTP:
    default:
      switch (strategy) {
        case SignInStrategy.YANDEX:
          return SignInStep.TOKEN_HANDLING;
        default:
          return SignInStep.EMAIL_OR_PHONE_ENTERING;
      }
  }
}

function addLoaderToSubmitButton(form: HTMLFormElement): void {
  const submitButton = getSubmitButton(form);
  if (submitButton instanceof HTMLButtonElement) {
    showButtonLoader(submitButton);
  }
}

function removeLoaderFromSubmitButton(form: HTMLFormElement): void {
  const submitButton = getSubmitButton(form);
  if (submitButton instanceof HTMLButtonElement) {
    hideButtonLoader(submitButton);
  }
}

function showEmailOrPhoneEnteringStep(form: HTMLFormElement) {
  hideElement(getBackButton(form));
  hideElement(getOneTimeCode(form));
  hideElement(getResendCodeButton(form));
  hideElement(getOtherButton(form));
  hideElement(getCaptcha(form));

  const step = SignInStep.EMAIL_OR_PHONE_ENTERING;
  updateTitle(form, step);
  updateSubtitle(form, step);

  toggleAlternativeBlock(form, true);

  resetFormBlocks(form, ["emailOrPhone"]);
  showElement(getAgreement(form));
  showElement(getSubmitButton(form));
}

function showOtpStep(form: HTMLFormElement, response: SignInNextStepResponse) {
  hideFormBlocks(form, ["emailOrPhone"]);
  hideElement(getAgreement(form));
  hideElement(getSubmitButton(form));
  hideElement(getCaptcha(form));

  toggleAlternativeBlock(form, false);

  updateTitle(form, response.step);
  updateSubtitle(form, response.step, response.otp.target);
  updateResendCodeButton(form, response.otp.cooldownSeconds);
  updateOtherButton(form, response.step);

  showOTCInput(form);

  showElement(getBackButton(form));
  showElement(getResendCodeButton(form));

  const otherButton = getOtherButton(form);

  const isForLoginByOther =
    response.step === SignInStep.EMAIL_OTP_FOR_LOGIN_BY_PHONE
    || response.step === SignInStep.PHONE_OTP_FOR_LOGIN_BY_EMAIL;
  isForLoginByOther ? hideElement(otherButton) : showElement(otherButton);
}

function showCaptchaStep(form: HTMLFormElement): void {
  hideFormBlocks(form, ["emailOrPhone"]);
  hideElement(getAgreement(form));
  hideElement(getResendCodeButton(form));
  hideElement(getOtherButton(form));
  hideElement(getOneTimeCode(form));

  toggleAlternativeBlock(form, false);

  const step = SignInStep.CAPTCHA;
  updateTitle(form, step);
  updateSubtitle(form, step);

  showElement(getCaptcha(form));
  resetFormBlocks(form, ['captcha']);
  showElement(getBackButton(form));
  showElement(getSubmitButton(form));
}

function showPhoneOtpUnavailableStep(form: HTMLFormElement) {
  hideFormBlocks(form, ["emailOrPhone"]);
  hideElement(getAgreement(form));
  hideElement(getSubmitButton(form));
  hideElement(getOneTimeCode(form));
  hideElement(getResendCodeButton(form));
  hideElement(getCaptcha(form));

  toggleAlternativeBlock(form, false);

  const step = SignInStep.PHONE_OTP_UNAVAILABLE;
  updateTitle(form, step);
  updateSubtitle(form, step);
  updateOtherButton(form, step);

  showElement(getBackButton(form));
  showElement(getOtherButton(form));
}

function showSuccess(form: HTMLFormElement): void {
  const otcInstance = getOneTimeCode(form).__otcInstance;
  otcInstance.showSuccess();

  updateResendCodeButton(form, 0);

  getBackButton(form).disabled = true;
  getResendCodeButton(form).disabled = true;
  getOtherButton(form).disabled = true;
}

function toggleAlternativeBlock(form: HTMLFormElement, toShow: boolean) {
  const alternativeBlock = form.querySelector(".auth__alternative");
  if (alternativeBlock instanceof HTMLElement) {
    toggleElementVisibility(alternativeBlock, toShow);
  }
}

function getAuthForm(): HTMLFormElement {
  const form = document.querySelector<HTMLFormElement>("#auth-form");

  if (form === null) {
    throw new Error("Auth form not found");
  }

  return form;
}

function getOneTimeCode(form: HTMLFormElement): HTMLElementWithOTC {
  const otcElement = form.querySelector<HTMLElement>(".one-time-code");

  if (otcElement === null) {
    throw new Error("One time code element not found");
  }

  return otcElement as HTMLElementWithOTC;
}

function getCaptcha(form: HTMLFormElement): HTMLElement {
  const captchaWidget = form.querySelector<HTMLElement>(".captcha-widget");

  if (captchaWidget === null) {
    throw new Error("Captcha widget element not found");
  }

  return captchaWidget;
}

function getResendCodeButton(form: HTMLFormElement): HTMLButtonElement {
  const button = form.querySelector<HTMLButtonElement>(".js-auth-resend-code-button");

  if (button === null) {
    throw new Error("Resend code button not found");
  }

  return button;
}

function getAgreement(form: HTMLFormElement): HTMLParagraphElement {
  const agreement = form.querySelector<HTMLParagraphElement>(".js-auth-agreement");

  if (agreement === null) {
    throw new Error("Agreement paragraph not found");
  }

  return agreement;
}

function getPrivacyConsentCheckbox(form: HTMLFormElement): HTMLInputElement {
  const checkbox = form.querySelector<HTMLInputElement>('.js-auth-agreement input[name="privacyConsent"]');

  if (checkbox === null) {
    throw new Error("Privacy consent checkbox not found");
  }

  return checkbox;
}

function getSubmitButton(form: HTMLFormElement): HTMLButtonElement {
  const button = form.querySelector<HTMLButtonElement>(".js-auth-submit-button");

  if (button === null) {
    throw new Error("Submit button not found");
  }

  return button;
}

function getOtherButton(form: HTMLFormElement): HTMLButtonElement {
  const button = form.querySelector<HTMLButtonElement>(".js-auth-other-button");

  if (button === null) {
    throw new Error("Other button not found");
  }

  return button;
}

function getBackButton(form: HTMLFormElement): HTMLButtonElement {
  const button = form.querySelector<HTMLButtonElement>(".js-auth-go-back-button");

  if (button === null) {
    throw new Error("Back button not found");
  }

  return button;
}

function getCloseButton(): HTMLLinkElement {
  const button = document.querySelector<HTMLLinkElement>(".js-auth-close-button");

  if (button === null) {
    throw new Error("Close button not found");
  }

  return button;
}

function getYandexButtonContainer(): HTMLElement | null {
  return document.querySelector<HTMLElement>(".js-button-ya");
}

function updateTitle(form: HTMLFormElement, step: SignInStep) {
  const title = form.querySelector<HTMLHeadingElement>(".js-auth-title");

  if (title instanceof HTMLHeadingElement) {
    let newTitle: string | null;
    switch (step) {
      case SignInStep.CAPTCHA:
        newTitle = title.dataset.captcha ?? null;
        break;
      case SignInStep.PHONE_OTP:
      case SignInStep.PHONE_OTP_FOR_LOGIN_BY_EMAIL:
        newTitle = title.dataset.phoneOtp ?? null;
        break;
      case SignInStep.PHONE_OTP_UNAVAILABLE:
        newTitle = title.dataset.phoneOtpUnavailable ?? null;
        break;
      case SignInStep.EMAIL_OTP:
      case SignInStep.EMAIL_OTP_FOR_LOGIN_BY_PHONE:
        newTitle = title.dataset.emailOtp ?? null;
        break;
      case SignInStep.EMAIL_OR_PHONE_ENTERING:
      default:
        newTitle = title.dataset.emailOrPhoneEntering ?? null;
        break;
    }

    if (newTitle !== null) {
      title.innerHTML = sanitizeInlineTextHtml(newTitle);
    }
  }
}

function updateSubtitle(form: HTMLFormElement, step: SignInStep, formattedTarget: string | null = null) {
  const subtitle = form.querySelector<HTMLParagraphElement>(".js-auth-subtitle");

  if (subtitle instanceof HTMLParagraphElement) {
    let newSubtitle: string;
    switch (step) {
      case SignInStep.CAPTCHA:
        newSubtitle = subtitle.dataset.captcha ?? "";
        break;
      case SignInStep.PHONE_OTP:
      case SignInStep.PHONE_OTP_FOR_LOGIN_BY_EMAIL:
        newSubtitle = subtitle.dataset.phoneOtp ?? "";
        break;
      case SignInStep.PHONE_OTP_UNAVAILABLE:
        newSubtitle = subtitle.dataset.phoneOtpUnavailable ?? "";
        break;
      case SignInStep.EMAIL_OTP:
      case SignInStep.EMAIL_OTP_FOR_LOGIN_BY_PHONE:
        newSubtitle = subtitle.dataset.emailOtp ?? "";
        break;
      case SignInStep.EMAIL_OR_PHONE_ENTERING:
      default:
        newSubtitle = subtitle.dataset.emailOrPhoneEntering ?? "";
        break;
    }

    subtitle.innerHTML = sanitizeInlineTextHtml(newSubtitle);

    if (formattedTarget !== null) {
      const targetElement = subtitle.querySelector<HTMLElement>(".js-formatted-target");
      if (targetElement instanceof HTMLElement) {
        targetElement.innerHTML = sanitizeInlineTextHtml(formattedTarget);
      }
    }

    subtitle.classList.toggle("hidden", !subtitle.hasChildNodes());
  }
}

function updateOtherButton(form: HTMLFormElement, step: SignInStep): void {
  const button = getOtherButton(form);

  let newContent: string | null = null;
  let isPrimary: boolean | null = null;

  switch (step) {
    case SignInStep.PHONE_OTP:
      newContent = button.dataset.phoneOtp ?? null;
      isPrimary = false;
      break;
    case SignInStep.PHONE_OTP_UNAVAILABLE:
      newContent = button.dataset.phoneOtpUnavailable ?? null;
      isPrimary = true;
      break;
    case SignInStep.EMAIL_OTP:
      newContent = button.dataset.emailOtp ?? null;
      isPrimary = false;
      break;
  }

  if (newContent !== null) {
    button.innerHTML = sanitizeInlineTextHtml(newContent);
  }

  if (isPrimary !== null) {
    button.classList.toggle("button_primary", isPrimary);
    button.classList.toggle("button_inverted", isPrimary);
    button.classList.toggle("button_tertiary", !isPrimary);
  }
}

function updateResendCodeButton(form: HTMLFormElement, cooldown: number): void {
  const button = getResendCodeButton(form);
  const instance = CooldownButton.getInstance(button);
  instance?.restart(cooldown);
}

function getFormBlockByInputName(form: HTMLFormElement, inputName: string): HTMLElement | null {
  const input = form.querySelector<HTMLElement>(`#${inputName}`);
  return input?.closest(".form-block") ?? null;
}

function enableInput(element: HTMLElement) {
  const input = element instanceof HTMLInputElement
    ? element
    : element.querySelector("input:not([type='hidden'])");

  if (input instanceof HTMLInputElement) {
    input.removeAttribute("disabled");
  }
}

function resetInput(element: HTMLElement) {
  const input = element instanceof HTMLInputElement
    ? element
    : element.querySelector("input:not([type='hidden'])");

  if (input instanceof HTMLInputElement) {
    input.value = "";
  }
}

function hideFormBlocks(form: HTMLFormElement, inputNames: string[]) {
  for (const inputName of inputNames) {
    const formBlock = getFormBlockByInputName(form, inputName);
    if (formBlock !== null) {
      hideElement(formBlock);
    }
  }
}

function resetFormBlocks(form: HTMLFormElement, inputNames: string[]) {
  for (const inputName of inputNames) {
    const formBlock = getFormBlockByInputName(form, inputName);
    if (formBlock !== null) {
      resetInput(formBlock);
      enableInput(formBlock);
      showElement(formBlock);
    }
  }
}

function showOTCInput(form: HTMLFormElement) {
  const otc = getOneTimeCode(form);
  const otcInstance = otc.__otcInstance;
  otcInstance.reset();

  showElement(otc);
  otcInstance.focusFirstInput();
}

function initCloseButton() {
  let storageReferrerUrl = sessionStorage.getItem("authReferrerPage");
  const referrerUrl = document.referrer;

  const isKnownReferrer = referrerUrl !== '';
  const isUserReferrer = referrerUrl.includes('/user/');

  if (isKnownReferrer && !isUserReferrer) {
    sessionStorage.setItem("authReferrerPage", referrerUrl);
    storageReferrerUrl = referrerUrl;
  }

  const closeButton = getCloseButton();
  closeButton.addEventListener("click", (e) => {
    if (storageReferrerUrl !== null) {
      e.preventDefault();
      e.stopPropagation();

      window.location.href = storageReferrerUrl;
    }

    sessionStorage.removeItem("authReferrerPage");
  })
}

function getUserData(): Data {
  return Object.assign(getUserNavigator(), getSearchParamsAuthData());
}

function getUserNavigator(): UserNavigator {
  const userNavigator: Record<string, unknown> = {};
  const navigators: UserNavigatorSources = { document, history, navigator, screen };

  const fields: UserNavigatorFields = {
    document: ['referrer'],
    history: ['length'],
    navigator: ['appName', 'appVersion', 'userAgent', 'language', 'platform', 'cookieEnabled'],
    screen: ['width', 'height', 'availWidth', 'availHeight', 'colorDepth', 'pixelDepth'],
  };

  for (const key in fields) {
    const section = key as keyof UserNavigatorSources;
    const source = navigators[section];

    for (const prop of fields[section]) {
      userNavigator[`${section}.${String(prop)}`] = source[prop as keyof typeof source];
    }
  }

  userNavigator['window.innerWidth'] = window.innerWidth;
  userNavigator['window.innerHeight'] = window.innerHeight;

  const date = new Date();
  userNavigator['timeOpened'] = date;
  userNavigator['timezone'] = date.getTimezoneOffset() / 60;

  return { "navigator": JSON.stringify(userNavigator) };
}

function getSearchParamsAuthData(): Data {
  const authSearchParamsKeys = ['role', 'mentor_id'] as const;
  const authSearchParamsData: Partial<Record<(typeof authSearchParamsKeys)[number], string>> = {};

  const searchParams = new URLSearchParams(window.location.search);

  for (const key of authSearchParamsKeys) {
    const value = searchParams.get(key);
    if (value !== null) {
      authSearchParamsData[key] = value;
    }
  }

  return authSearchParamsData;
}

let isYandexInitInProgress = false;

async function initYandexAuthSDK(signUpData: FormData): Promise<SignInResponse | null> {
  if (isYandexInitInProgress) {
    return null;
  }

  isYandexInitInProgress = true;
  try {
    if (!window.location.href.includes("/ru")) {
      return null;
    }

    const mql = window.matchMedia("(max-width: 768px)");

    const result = await window.YaAuthSuggest.init(
      {
        client_id: getYandexAuthClientId(),
        response_type: "token",
        redirect_uri: `${window.origin}/user/yandex-suggest-token`
      },
      window.origin,
      {
        view: "button",
        parentId: "ya-auth-button",
        buttonSize: mql.matches ? "xxl" : "m",
        buttonView: "main",
        buttonTheme: "light",
        buttonBorderRadius: "8",
        buttonIcon: "ya",
      }
    );

    if (result?.code === "in_progress") {
      return null;
    }

    const tokenResult = await result?.handler?.();
    if (!tokenResult?.access_token) {
      console.warn("YandexAuth: no access token received");
      return null;
    }

    signUpData.append("token", tokenResult.access_token);

    try {
      const formData = new FormData(getAuthForm());

      const dataKeysToAppend = [
        "agreement",
        "marketingConsentVersion"
      ];

      for (const key of dataKeysToAppend) {
        const value = formData.get(key);
        if (value !== null) {
          signUpData.append(key, value);
        }
      }
    } catch (e) {
      console.warn("YandexAuth: data from form is not append");
    }

    return await SignIn.signIn(SignInStrategy.YANDEX, SignInStep.TOKEN_HANDLING, signUpData);
  } catch (error) {
    console.error("Init yandex auth SDK error", error);
    return null;
  } finally {
    isYandexInitInProgress = false;
  }
}

function getYandexAuthClientId(): string | null {
  return document.head.querySelector<HTMLMetaElement>('meta[name="ya-auth-suggest-client-id"]')?.content ?? null;
}